import { useCallback, useContext, useMemo, useState } from "react";
import BaseModel from "base/BaseModel";
import _ from "lodash";
import { APIRequestGenerator } from "base/BaseActions";
import APIHelper from "base/APIHelper";
import { permissionHelper } from "base/PermissionHelper";
import { LEVELS } from "constants/permissions";
import { COMPANY_TYPE } from "constants/base";
import GeneralModel from "./generalModel";

const module = "user";

const useUserModel = (savedModelInstance = {}) => {
  // Region External Hooks

  const { navigate, TOAST, ALERT } = useContext(GeneralModel.Context);

  // End Region

  // Region State, Memos, Refs

  const [token, setToken] = useState(savedModelInstance?.token);
  const [userData, setUserData] = useState(savedModelInstance?.userData);

  const isLoggedIn = useMemo(() => !!userData?._id, [userData?._id]);
  const Organization = useMemo(() => userData?.Organization?._id, [userData]);
  const organizationData = useMemo(() => userData?.Organization, [userData]);

  const grantedActions = useMemo(() => {
    if (_.isEmpty(userData)) {
      return [];
    }
    return permissionHelper(userData?.UserGroup);
  }, [userData]);

  const grantedMenuItems = useMemo(() => {
    if (!_.isEmpty(grantedActions)) {
      return grantedActions?.getGrantedMenuItems();
    }
    return [];
  }, [grantedActions]);

  // End Region

  // Region API Calls

  function cleanUserData(data) {
    const { UserGroup, Company } = data;
    const { permissions = [], level } = UserGroup || {};

    // Organization
    let availableLevels = [LEVELS.ORGANIZATION, LEVELS.COMPANY, LEVELS.SITE];
    let availableCompanyTypes = [COMPANY_TYPE.CLIENT, COMPANY_TYPE.MAIN_CON];

    // Government
    if (level === LEVELS.ORGANIZATION && !UserGroup.isDefault) {
      availableLevels = [LEVELS.ORGANIZATION];
    }

    // Company
    if (level === LEVELS.COMPANY) {
      if (Company?.type === COMPANY_TYPE.MAIN_CON) {
        availableLevels = [LEVELS.COMPANY, LEVELS.SITE];
      } else {
        availableLevels = [LEVELS.COMPANY];
      }

      availableCompanyTypes = [Company?.type];
    }

    // Site
    if (level === LEVELS.SITE) {
      availableLevels = [LEVELS.SITE];
      availableCompanyTypes = [];
    }

    const allowSearchMainCon =
      level === LEVELS.ORGANIZATION ||
      (level === LEVELS.COMPANY && Company?.type === COMPANY_TYPE.CLIENT);

    return {
      ...data,
      permissions,
      level,
      availableLevels,
      availableCompanyTypes,
      allowSearchMainCon,
    };
  }

  async function login(input) {
    const { url } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/Auth/login`,
      null,
      null,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        input,
        true,
        null,
      );

      if (response?.data?.token) {
        setToken(response.data.token);
      }
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function logout() {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/Auth/logout`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        null,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setToken(null);
      setUserData({});

      const modal = window.document.getElementById("styledDialog");
      if (modal) {
        modal.style.display = "none";
      }
      TOAST.closeAll();
      ALERT.close();
      window.localStorage.removeItem("UserModel");
      navigate("/", { replace: true });
    }
  }

  async function getMe(inputToken = token) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/getMe`,
      null,
      inputToken,
    );
    try {
      const { records } = await APIHelper(
        "GET",
        url.toString(),
        null,
        true,
        headers,
      );
      const [record] = records || {};
      const data = cleanUserData(record);

      if (data) {
        setUserData(data);
      }
      return Promise.resolve(data);
    } catch (error) {
      setUserData({});
      await logout();
      return Promise.reject(error);
    }
  }

  async function getList(params) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}`,
      { Organization, ...params },
      token,
    );
    try {
      const response = await APIHelper(
        "GET",
        url.toString(),
        null,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getOne(id, params) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}`,
      params,
      token,
    );
    try {
      const response = await APIHelper(
        "GET",
        url.toString(),
        null,
        true,
        headers,
      );

      const { records } = response || {};
      const [record = {}] = records || [];

      return Promise.resolve(record);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function createOne(data, platform = "cms") {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        { Organization, platform, ...data },
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function updateOne(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function deleteOne(id) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "DELETE",
        url.toString(),
        null,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function generateQRCodeString(id) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/generateQRCodeString`,
      null,
      token,
    );
    try {
      const { data = {} } = await APIHelper(
        "PUT",
        url.toString(),
        null,
        true,
        headers,
      );
      const { url: qrCodeString = "" } = data || {};

      return Promise.resolve(qrCodeString);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getSchema() {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/schema`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "GET",
        url.toString(),
        null,
        true,
        headers,
      );

      const { schema = {} } = response || {};

      return Promise.resolve(schema);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function changePassword(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/changePassword`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function createAuth(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/createAuth`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function checkPhoneNumber(data, platform = "cms") {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/checkPhoneNumber`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        { Organization, platform, ...data },
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function checkAuthUsername(data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/Auth/checkUsername`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        { Organization, ...data },
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function deleteAllDocuments(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/deleteAllDocuments`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function checkHKID(data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/checkHKID`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        { Organization, hkid: data },
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function updateMe(data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/updateMe`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function updateProfiles(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/updateProfiles`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function importAppUsers(data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/importAppUsers`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function downloadAppUserImportTemplate(data) {
    const { returnURL } = data;
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/downloadAppUserImportTemplate`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "POST",
        url.toString(),
        data,
        true,
        headers,
        false,
        !returnURL,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function addToBlacklist(id, data) {
    const { url, headers } = APIRequestGenerator(
      `${process.env.REACT_APP_HOST}/${module}/${id}/addToBlacklist`,
      null,
      token,
    );
    try {
      const response = await APIHelper(
        "PUT",
        url.toString(),
        data,
        true,
        headers,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // End Region

  // Region Callbacks

  const onSaveInstanceState = useCallback(
    () => ({
      token,
      userData,
    }),
    [token, userData],
  );

  // End Region

  return {
    token,
    userData,
    Organization,
    organizationData,
    isLoggedIn,
    grantedMenuItems,
    grantedActions,

    logout,
    onSaveInstanceState,

    // API Calls
    login,
    getMe,
    getList,
    getOne,
    createOne,
    updateOne,
    deleteOne,
    generateQRCodeString,
    getSchema,
    changePassword,
    createAuth,
    checkPhoneNumber,
    checkAuthUsername,
    deleteAllDocuments,
    checkHKID,
    updateMe,
    updateProfiles,
    importAppUsers,
    downloadAppUserImportTemplate,
    addToBlacklist,
  };
};

const UserModel = BaseModel(useUserModel, "UserModel");

export { useUserModel };
export default UserModel;
